import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LocationPickerArea } from './style';
import { useTranslation } from 'react-i18next';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], 15);
    }
  }, [center, map]);
  return null;
}

function LocationMarker({ position, setPosition, onMapClick }) {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });

  return position === null ? null : <Marker position={position} />;
}

const LocationPicker = ({ 
  latitude, 
  longitude, 
  postalCode: initialPostalCode,
  onLocationChange,
  height = '400px'
}) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [postalCodeInput, setPostalCodeInput] = useState('');
  const [searchingPostal, setSearchingPostal] = useState(false);
  const [foundPostalCode, setFoundPostalCode] = useState('');
  const [locationInfo, setLocationInfo] = useState('');
  const [locationMessage, setLocationMessage] = useState('');
  const [mapKey, setMapKey] = useState(0); // Para forzar re-render del mapa

  // Indonesia center (Jakarta)
  const defaultCenter = { lat: -6.2088, lng: 106.8456 };
  const center = position || (latitude && longitude ? { lat: latitude, lng: longitude } : defaultCenter);

  useEffect(() => {
    if (latitude && longitude) {
      setPosition({ lat: latitude, lng: longitude });
    }
  }, [latitude, longitude]);

  const handleMapClick = async (latlng) => {
    setFoundPostalCode('');
    setLocationInfo('');
    setLocationMessage('');
    setError('');
    
    // Actualizar posición en el padre
    if (onLocationChange) {
      onLocationChange(latlng.lat, latlng.lng, '');
    }
    
    // Buscar el código postal del punto clickeado
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&addressdetails=1`
      );
      const data = await response.json();
      const address = data.address || {};
      const postalCode = address.postcode || '';
      
      // Extraer información de ubicación
      const locationParts = [];
      if (address.city) locationParts.push(address.city);
      else if (address.town) locationParts.push(address.town);
      else if (address.village) locationParts.push(address.village);
      else if (address.municipality) locationParts.push(address.municipality);
      
      if (address.state) locationParts.push(address.state);
      else if (address.province) locationParts.push(address.province);
      
      const locationText = locationParts.join(', ');
      
      if (postalCode) {
        setFoundPostalCode(postalCode);
        setLocationInfo(locationText || t('location_info_not_found'));
        if (onLocationChange) {
          onLocationChange(latlng.lat, latlng.lng, postalCode);
        }
      } else {
        setFoundPostalCode(t('no_postal_code_found'));
        setLocationInfo(locationText || '');
      }
    } catch (err) {
      // console.error('Geocoding error:', err);
      setFoundPostalCode(t('postal_code_search_error'));
    }
  };

  const handleGetCurrentLocation = () => {
    setLoading(true);
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        };
        setPosition(newPos);
        setLocationMessage(t('use_current_location'));
        setLoading(false);
      },
      (err) => {
        setError(t('geolocation_error'));
        setLoading(false);
        // console.error(err);
      }
    );
  };

  const handleSearchPostalCode = async () => {
    const trimmedCode = postalCodeInput.trim();
    
    // Validar formato de código postal indonesio (5 dígitos)
    if (!trimmedCode) {
      setError(t('postal_code_required'));
      return;
    }

    if (!/^\d{5}$/.test(trimmedCode)) {
      setError(t('postal_code_invalid_format'));
      return;
    }

    // Validar que sea un código postal indonesio válido (rango 10000-99999)
    const codeNumber = parseInt(trimmedCode);
    if (codeNumber < 10000 || codeNumber > 99999) {
      setError(t('postal_code_invalid_range'));
      return;
    }

    setSearchingPostal(true);
    setError('');

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${trimmedCode}&country=Indonesia&format=json&addressdetails=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        const address = result.address || {};
        const newPos = {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon)
        };
        
        // Extraer información de ubicación
        const locationParts = [];
        if (address.city) locationParts.push(address.city);
        else if (address.town) locationParts.push(address.town);
        else if (address.village) locationParts.push(address.village);
        else if (address.municipality) locationParts.push(address.municipality);
        
        if (address.state) locationParts.push(address.state);
        else if (address.province) locationParts.push(address.province);
        
        const locationText = locationParts.join(', ');
        
        setPosition(newPos);
        setPostalCodeInput(trimmedCode);
        setFoundPostalCode(trimmedCode);
        setLocationInfo(locationText || t('location_info_not_found'));
        setError('');
        setMapKey(prev => prev + 1); // Forzar re-render del mapa
        
        if (onLocationChange) {
          onLocationChange(newPos.lat, newPos.lng, trimmedCode);
        }
      } else {
        setError(t('postal_code_not_found'));
      }
    } catch (err) {
      setError(t('postal_code_search_error'));
      // console.error(err);
    } finally {
      setSearchingPostal(false);
    }
  };

  return (
    <LocationPickerArea>
      <div className="map-controls">
        <button 
          type="button"
          onClick={handleGetCurrentLocation}
          disabled={loading}
          className="location-btn"
        >
          📍 {loading ? t('getting_location') : t('get_location')}
        </button>
        {error && <span className="error-text">{error}</span>}
        {locationMessage && <span className="success-text">{locationMessage}</span>}
      </div>

      <div className="location-layout">
        <div className="map-section">
          <MapContainer
            key={mapKey}
            center={[center.lat, center.lng]}
            zoom={position ? 15 : 12}
            style={{ height: '350px', width: '100%', borderRadius: '12px' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapCenterUpdater center={position} />
            <LocationMarker position={position} setPosition={setPosition} onMapClick={handleMapClick} />
          </MapContainer>
        </div>

        <div className="postal-section">
          <div className="postal-input-group">
            <label>🏤 {t('postal_code')}</label>
            <input
              type="text"
              placeholder={t('postal_code_placeholder')}
              value={postalCodeInput}
              onChange={(e) => {
                const value = e.target.value;
                // Solo permitir números y máximo 5 dígitos
                if (/^\d{0,5}$/.test(value)) {
                  setPostalCodeInput(value);
                }
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchPostalCode()}
              maxLength={5}
            />
            <button
              type="button"
              onClick={handleSearchPostalCode}
              disabled={searchingPostal || postalCodeInput.length !== 5}
            >
              {searchingPostal ? t('searching') : `🔍 ${t('search_location')}`}
            </button>
          </div>

          <div className="info-text">
            💡 {t('postal_code_help')}
          </div>

          {foundPostalCode && (
            <div className="postal-result">
              <strong>📮 {t('postal_code_found')}:</strong>
              <div className="postal-code-display">{foundPostalCode}</div>
              {locationInfo && (
                <div className="location-info">
                  📍 {locationInfo}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </LocationPickerArea>
  );
};

export default LocationPicker;
